import type { NavigationGuard } from 'vue-router'

import type { DocType } from '@/constants/doctypes'
import type { Permission } from '@/types/external/2work/api/response/getDoctypePermissions'
import type { RedirectRoute, RedirectRoutePath } from '@/utilities/routers'

import { useAuthStore } from '@/stores/auth'
import { useErrorStore } from '@/stores/errorStore'
import { useSidebarStore } from '@/stores/sidebar'
import { useSimpleNotificationStore } from '@/stores/simpleNotificationStore'
import { translationFunctionDefault } from '@/utilities/translations'

import { redirectAuth } from './routes/routeAuth'

type GlobalNavigationGuardFunction = NavigationGuard

/** Returns the first accessible route from the sidebar or null */
const pickFirstMenuLink = (): null | RedirectRoute => {
  const sidebar = useSidebarStore()
  return sidebar.getFirstMenuLink || null
}

/** Checks doctype permissions for the target route and sets error store if needed */
const checkPermissionsForRoute = (
  toRoute: Parameters<NavigationGuard>[0],
  auth: ReturnType<typeof useAuthStore>,
  errorStore: ReturnType<typeof useErrorStore>,
  backRoutePath?: string,
): void => {
  try {
    for (const record of toRoute.matched) {
      // Detail routes are validated via document-level permission checks below.
      // This preserves direct access to explicitly shared/assigned records
      // without exposing list/module access for the whole doctype.
      if (record.meta.detail) {
        // eslint-disable-next-line no-continue
        continue
      }

      const dts = record.meta.doctypes
      const { if_owner } = record.meta
      if (dts && typeof dts === 'object') {
        for (const [doctype, requiredPerms] of Object.entries(dts)) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          const ok = auth.hasOnePermission(doctype as DocType, requiredPerms, false, if_owner)
          // eslint-disable-next-line max-depth
          if (!ok) {
            errorStore.setError('403', backRoutePath)
            return
          }
        }
      }
    }
  } catch (errorObj) {
    console.error('[Guard] Permission check failed:', errorObj)
    errorStore.setError('500')
  }
}

const resolveRouteParamValue = (value: unknown): null | string => {
  if (typeof value === 'string' && value.length > 0) {
    return value
  }

  if (Array.isArray(value) && typeof value[0] === 'string' && value[0].length > 0) {
    return value[0]
  }

  return null
}

const isInitialNavigation = (fromRoute: Parameters<NavigationGuard>[1]): boolean => {
  return fromRoute.matched.length === 0
}

const translateDoctypeLabel = (
  tfDefault: ReturnType<typeof translationFunctionDefault>,
  doctype: DocType,
): string => {
  return tfDefault(`doctypes.${doctype}`, { defaultValue: doctype })
}

const translatePermissionLabel = (
  tfDefault: ReturnType<typeof translationFunctionDefault>,
  permission: Permission,
): string => {
  return tfDefault(`permissions.${permission}`, { defaultValue: permission })
}

interface DetailPermissionCheckResult {
  allowed: boolean
  detailMeta: NonNullable<Parameters<NavigationGuard>[0]['matched'][number]['meta']['detail']>
  reason: 'denied' | 'missing-doc' | 'missing-param'
  routeName: Parameters<NavigationGuard>[0]['matched'][number]['name']
  routeParam: string
}

type DetailPermissionCheckStatus = 'allowed' | 'denied' | 'failed' | 'missing-doc'

const checkDetailPermissionsForRoute = async (
  toRoute: Parameters<NavigationGuard>[0],
  auth: ReturnType<typeof useAuthStore>,
  simpleNotificationStore: ReturnType<typeof useSimpleNotificationStore>,
): Promise<DetailPermissionCheckStatus> => {
  const tfDefault = translationFunctionDefault()

  try {
    const detailChecks = toRoute.matched.flatMap((record) => {
      const detailMeta = record.meta.detail
      if (!detailMeta) return []

      return [{ detailMeta, routeName: record.name }]
    })

    const detailResults = await Promise.all(
      detailChecks.map(async ({ detailMeta, routeName }): Promise<DetailPermissionCheckResult> => {
        const routeParam = detailMeta.param ?? 'id'
        const docname = resolveRouteParamValue(toRoute.params[routeParam])

        if (!docname) {
          return {
            allowed: false,
            detailMeta,
            reason: 'missing-param',
            routeName,
            routeParam,
          }
        }

        const permissionCheck = await auth.hasDocumentPermission(
          detailMeta.doctype,
          docname,
          detailMeta.perm_type,
        )

        if (!permissionCheck.exists) {
          return {
            allowed: false,
            detailMeta,
            reason: 'missing-doc',
            routeName,
            routeParam,
          }
        }

        return {
          allowed: permissionCheck.has_permission,
          detailMeta,
          reason: 'denied',
          routeName,
          routeParam,
        }
      }),
    )

    const firstRejectedCheck = detailResults.find((result) => !result.allowed)
    if (!firstRejectedCheck) {
      return 'allowed'
    }

    if (firstRejectedCheck.reason === 'missing-param') {
      console.warn('[Guard] Missing route param for detail permission check.', {
        param: firstRejectedCheck.routeParam,
        routeName: firstRejectedCheck.routeName,
      })
      simpleNotificationStore.triggerSimpleNotificationError(
        tfDefault('routeDetailPermission.checkFailed'),
      )
      return 'failed'
    }

    if (firstRejectedCheck.reason === 'missing-doc') {
      return 'missing-doc'
    }

    simpleNotificationStore.triggerSimpleNotificationError(
      tfDefault('routeDetailPermission.denied', {
        doctype: translateDoctypeLabel(tfDefault, firstRejectedCheck.detailMeta.doctype),
        permission: translatePermissionLabel(tfDefault, firstRejectedCheck.detailMeta.perm_type),
      }),
    )
    return 'denied'
  } catch (errorObj) {
    console.error('[Guard] Detail permission check failed:', errorObj)
    simpleNotificationStore.triggerSimpleNotificationError(
      tfDefault('routeDetailPermission.checkFailed'),
    )
    return 'failed'
  }
}

/** Logged in? Then initialize permissions & return first menu link (or null) */
const resolveHomeAfterAuth = async (): Promise<null | RedirectRoute> => {
  const auth = useAuthStore()
  await auth.initAccessibleModules()
  const sidebar = useSidebarStore()
  await sidebar.initializeSidebarContext()
  return pickFirstMenuLink()
}

/** Behavior on the login page */
const handleLoginRoute = async (
  hasSession: boolean,
  isLogout: boolean,
): Promise<RedirectRoute | true> => {
  // Not logged in or explicit logout → show login
  if (!hasSession || isLogout) return true

  // Logged in → immediately go to first accessible menu link
  const first = await resolveHomeAfterAuth()
  return first ?? redirectAuth // Fallback: Login (prevents 404)
}

/** Behavior on protected pages (requireAuth: true) */
const handleProtectedRoute = async (
  to: Parameters<NavigationGuard>[0],
  from: Parameters<NavigationGuard>[1],
  hasSession: boolean,
): Promise<false | RedirectRoutePath | true> => {
  if (!hasSession) return redirectAuth

  const auth = useAuthStore()
  const errorStore = useErrorStore()
  const simpleNotificationStore = useSimpleNotificationStore()
  const backRoutePath = isInitialNavigation(from) ? undefined : from.fullPath
  await auth.initAccessibleModules()
  const sidebar = useSidebarStore()
  await sidebar.initializeSidebarContext()
  checkPermissionsForRoute(to, auth, errorStore, backRoutePath)
  if (errorStore.errorCode) {
    if (isInitialNavigation(from)) {
      return true
    }

    return false
  }

  const detailPermissionStatus = await checkDetailPermissionsForRoute(
    to,
    auth,
    simpleNotificationStore,
  )
  if (detailPermissionStatus === 'allowed') {
    return true
  }

  if (detailPermissionStatus === 'missing-doc') {
    errorStore.setError('404', backRoutePath)
  } else {
    errorStore.setError(detailPermissionStatus === 'failed' ? '500' : '403', backRoutePath)
  }

  if (isInitialNavigation(from)) {
    return true
  }

  return false
}

/** Main Guard */
const authNavigationGuard: GlobalNavigationGuardFunction = async (to, from) => {
  const errorStore = useErrorStore()
  // Keep the current page visible during in-app navigations to avoid flicker.
  errorStore.setRouteResolutionPending(isInitialNavigation(from))
  errorStore.resetError()

  const auth = useAuthStore()

  // Reset logout redirect flag (one-time)
  if (auth.isLogoutRedirect) auth.isLogoutRedirectReset()

  const hasSession = await auth.isValidUserSession()
  const isLogin = to.path === redirectAuth.path
  const isLogout = isLogin && Boolean(to.query['logout'])

  // Login page
  if (isLogin) {
    return await handleLoginRoute(hasSession, isLogout)
  }

  // requireAuth pages
  const needsAuth = Boolean(to.meta.requireAuth)
  if (needsAuth) {
    return await handleProtectedRoute(to, from, hasSession)
  }

  // Public pages
  return true
}

export { authNavigationGuard }
